import { startTransition, useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useUIStore } from '../../stores/ui.store';
import { Icon, type IconName } from '../ui/Icon';
import { useQuery } from '@tanstack/react-query';
import { useActionStream } from '../../features/oggi/useActionStream';
import { tasksSummaryQuery } from '../../features/tasks/tasksApi';
import { ModuleKey, hasAtLeast, PermissionLevel } from '@masamune/shared';
import { cn } from '../../lib/utils';
import { isFrozen } from '../../lib/frozen';
import { ADMIN_PAGES, canSeeAdminPage, type AdminPageKey } from '../../lib/adminRoutes';
import { adminOverviewQuery, daysFromToday } from '../../features/admin/adminApi';
import { useSidebarPill } from './useSidebarPill';

/**
 * Esegue `fn` appena il browser ha disegnato il prossimo fotogramma: il tempo
 * di consegnare al compositor l'animazione della pill, prima che la pagina
 * nuova occupi il main thread. Il timeout e' la rete se rAF non arriva.
 */
function afterNextPaint(fn: () => void) {
  let done = false;
  const run = () => { if (!done) { done = true; fn(); } };
  requestAnimationFrame(() => setTimeout(run, 0));
  setTimeout(run, 100);
}

interface NavItem {
  key: string;
  to: string;
  label: string;
  icon: IconName;
  module: ModuleKey;
  count?: number;
  /** Etichetta divisione (neutra): data hook, NON un colore — l'accento resta il viola Masamune */
  div?: 'mkt' | 'prod' | 'design' | 'talent';
  /** Sotto-voce: `module` serve solo al badge "In pausa" (Fase 0); senza, eredita quello del padre */
  sub?: { key: string; to: string; label: string; icon: IconName; module?: ModuleKey; div?: 'mkt' | 'prod' | 'design' | 'talent' }[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    title: 'Operativo',
    items: [
      { key: 'oggi', to: '/', label: 'Oggi', icon: 'dashboard', module: ModuleKey.DASHBOARD },
      { key: 'tasks', to: '/tasks', label: 'Task', icon: 'tasks', module: ModuleKey.TASKS },
      { key: 'deals', to: '/trattative', label: 'Trattative', icon: 'handshake', module: ModuleKey.TRATTATIVE },
      // "Contatti" e non piu' "CRM": dentro ci sono clienti, potenziali e
      // professionisti insieme, e la voce deve dire quello che c'e'.
      { key: 'crm',       to: '/crm', label: 'Contatti', icon: 'users',    module: ModuleKey.CRM },
      { key: 'suppliers', to: '/suppliers', label: 'Professionisti', icon: 'brief', module: ModuleKey.SUPPLIERS },
    ],
  },
  {
    // Le tre divisioni erano tre voci per la stessa identica pagina: adesso
    // sono un filtro dentro Progetti. Talent Sourcing resta a sé perché è
    // un'altra cosa (persone e corsi, non commesse): deciso da Lorenzo il 19/9.
    // Il permesso è CRM perché è quello che già governa la creazione di un
    // progetto lato API: gatarla su MARKETING avrebbe nascosto la sezione
    // proprio a chi l'ha chiesta.
    title: 'Lavoro',
    items: [
      { key: 'progetti', to: '/progetti', label: 'Progetti', icon: 'cube', module: ModuleKey.CRM },
      { key: 'talent',   to: '/talent',   label: 'Talent Sourcing', icon: 'talent', module: ModuleKey.TALENT, div: 'talent' },
    ],
  },
  {
    title: 'Back-office',
    items: [
      // Una sola voce per i soldi: la Contabilità è confluita in Amministrazione
      // (Fatture › Documenti di vendita). Le route /invoices e /quotes restano
      // vive per i vecchi link e per le pagine di dettaglio, ma fuori dal menu.
      // Le sei pagine dell'Amministrazione sono sottovoci, e si vedono solo
      // quando si è dentro /admin: altrimenti il Back-office si allungherebbe
      // di sei righe su tutte le pagine (r-proposta-claude.md § 1.3).
      {
        key: 'admin', to: '/admin', label: 'Amministrazione', icon: 'landmark', module: ModuleKey.AMMINISTRAZIONE,
        sub: ADMIN_PAGES.map((p) => ({ key: p.key, to: p.to, label: p.label, icon: p.icon })),
      },
      // "Abbonamenti" e non "Servizi gestiti" (Lorenzo, 19/9): dentro ci sono
      // gli abbonamenti Stripe piu' i canoni fatturati a mano, e il nome deve
      // dire quello che si trova.
      { key: 'services', to: '/services', label: 'Abbonamenti',      icon: 'repeat',   module: ModuleKey.SERVICES },
      { key: 'vault',    to: '/vault',    label: 'Password Vault',   icon: 'lock',     module: ModuleKey.VAULT },
      { key: 'report',   to: '/report',   label: 'Report',           icon: 'chart',    module: ModuleKey.REPORT },
      { key: 'settings', to: '/settings', label: 'Impostazioni',     icon: 'settings', module: ModuleKey.SETTINGS },
    ],
  },
];

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const canvas = useUIStore((s) => s.canvas);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const glassRef = useRef<HTMLDivElement>(null);

  // Al clic la sidebar si muove SUBITO verso la voce scelta (pill e sottovoci),
  // e la pagina nuova parte un fotogramma dopo, in una transition: cosi' il
  // suo render non ruba i fotogrammi dell'animazione. Finche' React non ha
  // raggiunto l'indirizzo nuovo, la voce attiva e' quella cliccata.
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const activePath = pendingPath ?? location.pathname;
  useEffect(() => {
    // Arrivati (o reindirizzati altrove): torna a comandare l'indirizzo vero
    setPendingPath((p) => (p != null && (location.pathname === p || location.pathname === window.location.pathname) ? null : p));
  }, [location]);
  const go = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    // Come fa Link: stessa pagina = replace, niente voce doppia nella cronologia
    const replace = location.pathname + location.search + location.hash === to;
    setPendingPath(to);
    afterNextPaint(() => startTransition(() => navigate(to, { replace })));
  };

  useSidebarPill(navRef, pillRef, glassRef, activePath, collapsed ? 'collapsed' : 'expanded');
  // Conteggio vivo per "Oggi" (critico + oggi) — stessa cache dello stream, zero fetch extra
  const { counts } = useActionStream();
  const oggiCount = counts.critical + counts.today;

  const canSee = (module: ModuleKey) => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.READ);
  };

  // Badge "Task" = scadono oggi + in ritardo dei MIEI task (stessa query dello
  // stream Oggi: cache condivisa, un fetch al minuto al massimo)
  const { data: taskSummary } = useQuery({ ...tasksSummaryQuery('mine'), enabled: canSee(ModuleKey.TASKS) });
  const tasksCount = (taskSummary?.dueToday ?? 0) + (taskSummary?.overdue ?? 0);

  // Pallini delle sottovoci dell'Amministrazione: solo conteggi (mai importi)
  // e solo dove c'è qualcosa da fare. Stessa cache dello stream di Oggi, che
  // l'overview la chiede già: zero fetch in più.
  const { data: adminOv } = useQuery({ ...adminOverviewQuery, enabled: canSee(ModuleKey.AMMINISTRAZIONE) });
  const adminBadge: Partial<Record<AdminPageKey, number>> = {
    fatture: adminOv?.fatture.overdueCount ?? 0,
    banca: adminOv?.fatture.daRiconciliare?.proposte ?? adminOv?.fatture.toReview.length ?? 0,
    // Scadenze fiscali da oggi ai prossimi 7 giorni
    fisco: (adminOv?.tasse.next ?? []).filter((t) => { const d = daysFromToday(t.dueDate); return d != null && d >= 0 && d <= 7; }).length,
  };

  return (
    <aside className="os-sidebar flex flex-col">
      <div className="sb-brand">
        {collapsed ? (
          <div className="sb-brand-mark" style={{ width: 'auto', padding: '0 10px' }}>
            <img src="/logos/m-white.svg" alt="Masamune" style={{ height: 14, width: 'auto', display: 'block' }} />
          </div>
        ) : (
          <>
            {/* Spina viola: sempre il logotipo bianco (quadratino viola + scritta bianca) */}
            <img src="/logos/masamune.svg" alt="Masamune" className="sb-brand-logo" />
            <div className="sb-brand-tag">OS v0.1</div>
          </>
        )}
      </div>

      <div ref={navRef} className="flex-1 py-1 overflow-y-auto relative">
        {/* Pill attiva che scivola tra le voci (posizione e misure: useSidebarPill); il vetro (figlio) è quello che "gelatina" */}
        <div ref={pillRef} className="sb-active-pill" aria-hidden="true">
          <div ref={glassRef} className="sb-active-pill-glass" />
        </div>
        {NAV.map((group) => {
          const visible = group.items.filter((it) => canSee(it.module));
          if (!visible.length) return null;
          return (
            <div className="sb-section" key={group.title}>
              {!collapsed && <div className="sb-section-label">{group.title}</div>}
              {visible.map((item) => {
                const isActive = activePath === item.to || (item.to !== '/' && activePath.startsWith(item.to + '/'));
                // Fase 0: la voce resta visibile e cliccabile (porta alla pagina
                // di pausa), ma smorzata e con badge "In pausa" al posto del conteggio
                const paused = isFrozen(item.module);
                return (
                  <div key={item.key}>
                    {/* Link e non NavLink: NavLink decide aria-current sulla location di
                        React, che durante la transition e' ancora quella vecchia */}
                    <Link
                      to={item.to}
                      onClick={go(item.to)}
                      className="sb-item"
                      data-div={item.div}
                      aria-current={isActive ? 'true' : 'false'}
                      aria-disabled={paused || undefined}
                      title={collapsed ? (paused ? `${item.label} (in pausa)` : item.label) : undefined}
                    >
                      <Icon name={item.icon} size={16} />
                      {!collapsed && (
                        <>
                          <span>{item.label}</span>
                          {paused
                            ? <span className="sb-count sb-count-paused">In pausa</span>
                            : item.key === 'oggi' && oggiCount > 0
                              ? <span className="sb-count">{oggiCount}</span>
                              : item.key === 'tasks' && tasksCount > 0
                                ? <span className="sb-count">{tasksCount}</span>
                                : item.count != null && <span className="sb-count">{item.count}</span>}
                        </>
                      )}
                    </Link>
                    {/* Le sottovoci si aprono solo dentro la loro sezione. Restano
                        montate e si aprono/chiudono con l'accordion (.sb-sub): da
                        chiuse sono alte 0 e invisibili anche alla tastiera. */}
                    {item.sub && !collapsed && (
                      <div className="sb-sub" data-sb-sub="" data-open={isActive ? 'true' : 'false'}>
                        <div className="sb-sub-inner">
                          <div className="sb-sub-list ml-6 pl-2 border-l border-line">
                            {item.sub
                              // Dipendenti chiede il livello ADMIN, non basta leggere
                              .filter((s) => item.key !== 'admin' || canSeeAdminPage(user, s.key as AdminPageKey))
                              .map((s, i) => {
                              const subPaused = isFrozen(s.module ?? item.module);
                              const badge = item.key === 'admin' ? adminBadge[s.key as AdminPageKey] ?? 0 : 0;
                              return (
                                <Link
                                  key={s.key}
                                  to={s.to}
                                  onClick={go(s.to)}
                                  className="sb-item sb-subitem"
                                  style={{ '--i': i } as CSSProperties}
                                  data-div={s.div}
                                  aria-current={isActive && activePath === s.to ? 'true' : 'false'}
                                  aria-disabled={subPaused || undefined}
                                  tabIndex={isActive ? undefined : -1}
                                >
                                  <Icon name={s.icon} size={14} />
                                  <span>{s.label}</span>
                                  {subPaused
                                    ? <span className="sb-count sb-count-paused">In pausa</span>
                                    : badge > 0 && <span className="sb-count">{badge}</span>}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {user && (
        <div className="sb-user">
          <div className="sb-avatar">{user.initials}</div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-text text-[13px] truncate">{user.firstName} {user.lastName}</div>
              <span className="sb-role">{user.role.name}</span>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
